/**
 * Streak Store
 * ─────────────
 * Tracks daily check-in streaks, streak freezes, and milestone celebrations.
 * Every day has a check-in — training days, rest days, and cross-training days
 * all count. Skipping a rest day check-in breaks the streak. This is intentional.
 *
 * Freeze timing: The Firebase Cloud Function (REQ-NOTIF-01) applies freezes at
 * midnight server-side. The client detects the resulting Firestore state on
 * hydration and shows the amber notice banner. applyFreeze() here is a fallback
 * for demo mode and offline.
 *
 * Pattern: optimistic local update → background Firestore write.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { localToday, localDateStr, localDateOffset } from './date-utils';
import { XP_PER_CHECKIN, XP_MILESTONE_BONUS, levelFromXp } from './xp';
import type { SubscriptionTier } from '@/types/user';

// ─── Types ───────────────────────────────────────────────────────────────────

/** 'checked-in': today already done. 'live': yesterday was last, today pending. 'broken': gap > 1 day. */
export type StreakStatus = 'checked-in' | 'live' | 'broken';

export interface StreakMilestone {
  day: number;
  label: string;
  celebration: 'confetti' | 'achievement-card' | 'coach-message' | 'full-screen' | 'shoutout' | 'special' | 'recap';
  reward: string;
}

/** A persistently earned milestone badge (REQ-STREAK-02). */
export interface EarnedBadge {
  day: number;                // FK into MILESTONES
  earnedAt: string | null;    // ISO timestamp; null = earned before this feature shipped
  streakAtEarning: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const MILESTONES: StreakMilestone[] = [
  { day: 3,   label: 'First Steps',          celebration: 'confetti',          reward: 'Encouragement from your coach' },
  { day: 7,   label: 'Weekly Warrior',        celebration: 'achievement-card',  reward: 'Badge unlocked' },
  { day: 14,  label: 'Two Weeks Strong',      celebration: 'coach-message',     reward: '+1 streak freeze' },
  { day: 30,  label: 'Monthly Machine',       celebration: 'full-screen',       reward: 'Coach style customization unlocked' },
  { day: 50,  label: 'Fifty and Fierce',      celebration: 'shoutout',          reward: 'Advanced analytics unlocked' },
  { day: 100, label: 'Century Club',          celebration: 'special',           reward: 'Priority coach responses unlocked' },
  { day: 365, label: 'Year of Commitment',    celebration: 'recap',             reward: 'Lifetime badge earned' },
];

/** Weekly freeze allowance per tier. Elite is effectively unlimited. */
const FREEZE_ALLOWANCE: Record<SubscriptionTier, number> = {
  free: 1,
  starter: 2,
  elite: 999,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns the Monday of the current week as YYYY-MM-DD (local timezone). */
function currentWeekMonday(): string {
  const today = new Date();
  const day = today.getDay(); // 0=Sun … 6=Sat
  const daysBack = day === 0 ? 6 : day - 1; // Make Mon=0
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysBack);
  return localDateStr(monday);
}

/** Number of calendar days between two YYYY-MM-DD strings (b - a). */
function daysBetween(a: string, b: string): number {
  const msPerDay = 86_400_000;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / msPerDay);
}

// ─── State Interface ──────────────────────────────────────────────────────────

interface StreakState {
  // Core streak
  streakCount: number;
  streakStartDate: string;        // ISO date — when the current streak started
  longestStreak: number;          // all-time best; never resets on break
  lastCheckInDate: string;        // ISO date — last successful check-in

  // Freeze management
  streakFreezes: number;          // available freezes remaining this week
  freezesWeekOf: string;          // Monday of the week these freezes are allocated to

  // UI notices (persist so they survive app restarts)
  pendingFreezeNotice: boolean;   // show amber "freeze used" banner on next open
  frozenForStreak: number | null; // streak count at time of freeze (for display)
  pendingMilestoneCelebration: number | null; // milestone day awaiting celebration
  celebratedMilestones: number[]; // milestones already triggered (no repeats)

  // Gamification (level is always derived from xpTotal — never stored)
  xpTotal: number;
  earnedBadges: EarnedBadge[];
  pendingLevelUp: number | null;  // new level awaiting celebration (local-only, not synced)

  // Actions
  checkIn: () => void;
  applyFreeze: () => void;
  dismissFreezeNotice: () => void;
  clearMilestoneCelebration: () => void;
  clearLevelUp: () => void;
  refreshFreezes: (tier: SubscriptionTier) => void;

  // Computed
  getStatus: () => StreakStatus;
  canUseFreeze: () => boolean;
  getFreezeBalance: (tier: SubscriptionTier) => { remaining: number; weeklyMax: number };
  getMilestoneForDay: (day: number) => StreakMilestone | undefined;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useStreakStore = create<StreakState>()(
  persist(
    (set, get) => ({
      streakCount: 0,
      streakStartDate: '',
      longestStreak: 0,
      lastCheckInDate: '',
      streakFreezes: 1,           // sensible default (free tier); refreshFreezes() calibrates this
      freezesWeekOf: '',
      pendingFreezeNotice: false,
      frozenForStreak: null,
      pendingMilestoneCelebration: null,
      celebratedMilestones: [],
      xpTotal: 0,
      earnedBadges: [],
      pendingLevelUp: null,

      // ── Core action: record today's check-in ────────────────────────────────
      checkIn: () => {
        const today = localToday();
        const {
          lastCheckInDate, streakCount, longestStreak,
          streakStartDate, celebratedMilestones,
          xpTotal, earnedBadges, streakFreezes,
        } = get();

        if (lastCheckInDate === today) return; // already checked in today
        // ↑ this guard also makes the XP award idempotent per day across all callers

        const yesterday = localDateOffset(-1);
        const consecutive = lastCheckInDate === yesterday;

        const newCount = consecutive ? streakCount + 1 : 1;
        const newStart = consecutive ? streakStartDate : today;
        const newLongest = Math.max(longestStreak, newCount);

        // Milestone detection — find the lowest uncelebrated milestone just reached
        const freshMilestone = MILESTONES.find(
          m => m.day === newCount && !celebratedMilestones.includes(m.day),
        );

        // XP: flat per check-in + milestone bonus; level always derived
        const newXp = xpTotal + XP_PER_CHECKIN + (freshMilestone ? XP_MILESTONE_BONUS : 0);
        const leveledUp = levelFromXp(newXp) > levelFromXp(xpTotal);

        const updates: Partial<StreakState> = {
          streakCount: newCount,
          streakStartDate: newStart,
          longestStreak: newLongest,
          lastCheckInDate: today,
          xpTotal: newXp,
        };

        const syncPatch: Record<string, unknown> = {
          streakCount: newCount,
          streakStartDate: newStart,
          longestStreak: newLongest,
          lastCheckInDate: today,
          xpTotal: newXp,
        };

        if (freshMilestone) {
          updates.pendingMilestoneCelebration = freshMilestone.day;
          updates.celebratedMilestones = [...celebratedMilestones, freshMilestone.day];
          updates.earnedBadges = [...earnedBadges, {
            day: freshMilestone.day,
            earnedAt: new Date().toISOString(),
            streakAtEarning: newCount,
          }];
          syncPatch.celebratedMilestones = updates.celebratedMilestones;
          syncPatch.earnedBadges = updates.earnedBadges;

          // Day-14 reward is real, not just display text: +1 streak freeze
          if (freshMilestone.day === 14) {
            updates.streakFreezes = streakFreezes + 1;
            syncPatch.streakFreezes = streakFreezes + 1;
          }
        }

        // Level-up celebration queues behind the milestone one (overlay shows
        // milestone first, level-up after dismiss). Local-only signal.
        if (leveledUp) {
          updates.pendingLevelUp = levelFromXp(newXp);
        }

        set(updates);

        // Background Firestore write (non-blocking)
        _syncStreak(syncPatch);
      },

      // ── Freeze: cover one missed day without breaking the streak ────────────
      // Called client-side for demo/offline. The Firebase Cloud Function is the
      // authoritative midnight trigger in production (REQ-NOTIF-01).
      applyFreeze: () => {
        const { streakCount, streakFreezes, getStatus } = get();
        const status = getStatus();

        // Only applies when streak would otherwise be broken AND freeze is available
        if (status !== 'broken' || streakFreezes <= 0) return;

        const yesterday = localDateOffset(-1);

        set({
          lastCheckInDate: yesterday,   // freeze "covers" yesterday — chain stays intact
          streakFreezes: streakFreezes - 1,
          pendingFreezeNotice: true,
          frozenForStreak: streakCount,
        });

        _syncStreak({
          lastCheckInDate: yesterday,
          streakFreezes: streakFreezes - 1,
          pendingFreezeNotice: true,
          frozenForStreak: streakCount,
        });
      },

      dismissFreezeNotice: () => {
        set({ pendingFreezeNotice: false, frozenForStreak: null });
      },

      clearMilestoneCelebration: () => {
        set({ pendingMilestoneCelebration: null });
      },

      clearLevelUp: () => {
        set({ pendingLevelUp: null });
      },

      // ── Weekly freeze budget refresh ─────────────────────────────────────────
      // Called from auth-store after login, once tier is known.
      // Also resets when the calendar week rolls over (Mon midnight).
      refreshFreezes: (tier) => {
        const { freezesWeekOf, streakFreezes } = get();
        const thisMonday = currentWeekMonday();

        if (freezesWeekOf === thisMonday) return; // already allocated this week

        // New week — reset to tier allowance
        set({
          streakFreezes: FREEZE_ALLOWANCE[tier],
          freezesWeekOf: thisMonday,
        });

        // If this is a brand-new store (no week recorded), just set the week baseline
        // without sync — the Firestore hydration is the source of truth on login.
        if (freezesWeekOf !== '') {
          _syncStreak({ streakFreezes: FREEZE_ALLOWANCE[tier], freezesWeekOf: thisMonday });
        } else {
          // First run: record the current week so the next login doesn't re-grant
          set({ freezesWeekOf: thisMonday, streakFreezes: Math.max(streakFreezes, FREEZE_ALLOWANCE[tier]) });
        }
      },

      // ── Computed ─────────────────────────────────────────────────────────────

      getStatus: (): StreakStatus => {
        const { lastCheckInDate, streakCount } = get();
        const today = localToday();

        if (lastCheckInDate === today) return 'checked-in';

        if (streakCount === 0 || lastCheckInDate === '') return 'broken';

        const yesterday = localDateOffset(-1);
        if (lastCheckInDate === yesterday) return 'live';

        // Gap > 1 day — server-side Cloud Function should have applied freeze if
        // available. If it hasn't yet (offline / race condition), treat as broken
        // so the UI can prompt the user to act.
        return 'broken';
      },

      canUseFreeze: (): boolean => {
        const { streakFreezes, getStatus } = get();
        return streakFreezes > 0 && getStatus() === 'broken';
      },

      getFreezeBalance: (tier): { remaining: number; weeklyMax: number } => ({
        remaining: get().streakFreezes,
        weeklyMax: FREEZE_ALLOWANCE[tier],
      }),

      getMilestoneForDay: (day) => MILESTONES.find(m => m.day === day),
    }),
    {
      name: 'kader-streak',
    },
  ),
);

// ─── Firestore Sync (lazy-loaded to avoid circular deps) ─────────────────────

/** Background write — never blocks the UI. Failures are non-critical (localStorage holds state). */
async function _syncStreak(patch: Record<string, unknown>): Promise<void> {
  try {
    const { syncStreak } = await import('./firestore-sync');
    await syncStreak(patch);
  } catch {
    // Firestore unavailable — localStorage is still intact
  }
}

// ─── Firestore Hydration ──────────────────────────────────────────────────────

/**
 * Merge Firestore streak data into the local store on login.
 * Firestore is authoritative (Cloud Function may have applied freezes overnight).
 * Called from auth-store after access is approved.
 */
export async function hydrateStreakFromFirestore(): Promise<void> {
  try {
    const { loadStreak } = await import('./firestore-sync');
    const data = await loadStreak();
    if (!data) return;

    const current = useStreakStore.getState();

    // Firestore wins for all numeric streak fields (Cloud Function is authoritative)
    const updates: Partial<StreakState> = {};

    if (typeof data.streakCount === 'number') updates.streakCount = data.streakCount;
    if (typeof data.streakStartDate === 'string') updates.streakStartDate = data.streakStartDate;
    if (typeof data.longestStreak === 'number') updates.longestStreak = data.longestStreak;
    if (typeof data.lastCheckInDate === 'string') updates.lastCheckInDate = data.lastCheckInDate;
    if (typeof data.streakFreezes === 'number') updates.streakFreezes = data.streakFreezes;
    if (typeof data.freezesWeekOf === 'string') updates.freezesWeekOf = data.freezesWeekOf;

    // Show freeze notice if Cloud Function fired overnight
    if (data.pendingFreezeNotice === true && !current.pendingFreezeNotice) {
      updates.pendingFreezeNotice = true;
      if (typeof data.frozenForStreak === 'number') updates.frozenForStreak = data.frozenForStreak;
    }

    // Gamification fields — Firestore wins when present
    if (typeof data.xpTotal === 'number') updates.xpTotal = data.xpTotal;
    if (Array.isArray(data.celebratedMilestones)) updates.celebratedMilestones = data.celebratedMilestones as number[];
    if (Array.isArray(data.earnedBadges)) updates.earnedBadges = data.earnedBadges as EarnedBadge[];

    // Pre-populate celebratedMilestones for existing streaks (avoid re-celebrating
    // milestones that predate this feature shipping).
    const incomingCount = (updates.streakCount ?? current.streakCount);
    const milestonesKnown = updates.celebratedMilestones ?? current.celebratedMilestones;
    if (milestonesKnown.length === 0 && incomingCount > 0) {
      updates.celebratedMilestones = MILESTONES
        .filter(m => m.day <= incomingCount)
        .map(m => m.day);
    }

    // One-time backfill for accounts that pre-date the gamification feature:
    // synthesize earned badges (no date known) and a conservative XP total from
    // the current streak. Pushed up in a single sync so it never re-runs.
    // NEVER sets pending celebration signals — backfilled badges arrive silently.
    const needsBadgeBackfill = !Array.isArray(data.earnedBadges) && incomingCount > 0;
    const needsXpBackfill = typeof data.xpTotal !== 'number' && incomingCount > 0;
    if (needsBadgeBackfill) {
      updates.earnedBadges = MILESTONES
        .filter(m => m.day <= incomingCount)
        .map(m => ({ day: m.day, earnedAt: null, streakAtEarning: m.day }));
    }
    if (needsXpBackfill) {
      updates.xpTotal = XP_PER_CHECKIN * incomingCount
        + XP_MILESTONE_BONUS * (updates.earnedBadges ?? current.earnedBadges).length;
    }

    if (Object.keys(updates).length > 0) {
      useStreakStore.setState(updates);
      console.log('[streak-store] Hydrated from Firestore');
    }

    if (needsBadgeBackfill || needsXpBackfill) {
      _syncStreak({
        ...(needsBadgeBackfill ? { earnedBadges: updates.earnedBadges } : {}),
        ...(needsXpBackfill ? { xpTotal: updates.xpTotal } : {}),
        ...(updates.celebratedMilestones ? { celebratedMilestones: updates.celebratedMilestones } : {}),
      });
      console.log('[streak-store] Gamification backfill synced');
    }
  } catch (err) {
    console.warn('[streak-store] Firestore hydration failed:', err);
  }
}

// ─── Coach context helper ─────────────────────────────────────────────────────

/**
 * Returns a short streak summary for injection into the coach system prompt.
 * Keeps the coach aware of the athlete's current streak without exposing full state.
 */
export function getStreakCoachContext(): string {
  const { streakCount, longestStreak, lastCheckInDate, xpTotal, earnedBadges } = useStreakStore.getState();
  const today = localToday();
  const checkedIn = lastCheckInDate === today;

  const parts: string[] = [];
  if (streakCount === 0) {
    parts.push('Athlete has no active streak.');
  } else {
    parts.push(`Current streak: ${streakCount} day${streakCount === 1 ? '' : 's'}.`);
    if (longestStreak > streakCount) parts.push(`Longest ever: ${longestStreak} days.`);
    if (!checkedIn) parts.push("Today's check-in is still pending.");
  }

  parts.push(`Level ${levelFromXp(xpTotal)} (${xpTotal} XP).`);
  const newest = earnedBadges[earnedBadges.length - 1];
  if (newest) {
    const milestone = MILESTONES.find(m => m.day === newest.day);
    if (milestone) parts.push(`Latest badge: ${milestone.label} (day ${milestone.day}).`);
  }

  return parts.join(' ');
}
